/**
 * Agents Store
 * Zustand store para gestión de estado de agentes
 */

import { create } from 'zustand';
import type {
  Agent,
  AgentFilters,
  AgentMetrics,
  AgentUpdateState,
  ServiceEventData,
  ServiceStatus,
} from '@/types/agent';

interface AgentsState {
  // Data
  agents: Agent[];
  selectedAgentId: string | null;
  agentMetrics: Record<string, AgentMetrics>;
  updateStates: Record<string, AgentUpdateState>;
  serviceEvents: Record<string, ServiceEventData | null>;
  /** IDs deleted locally; stale WS snapshots must not resurrect them. */
  /** Server revision at which the ID was locally revoked. */
  revokedAgentIds: Record<string, number>;
  lifecycleRevision: number;

  // Filters
  filters: AgentFilters;

  // Actions
  setAgents: (agents: Agent[], lifecycleRevision?: number, canonical?: boolean) => void;
  updateAgent: (agent: Agent) => void;
  updateAgentMetrics: (agentId: string, metrics: AgentMetrics) => void;
  setUpdateState: (agentId: string, state: AgentUpdateState) => void;
  clearUpdateState: (agentId: string) => void;
  setServiceEvent: (agentId: string, event: ServiceEventData | null) => void;
  clearServiceEvent: (agentId: string) => void;
  updateAgentServiceStatus: (agentId: string, status: ServiceStatus) => void;
  removeAgent: (agentId: string, lifecycleRevision?: number) => void;
  restoreAgent: (agentId: string) => void;
  selectAgent: (agentId: string | null) => void;
  setFilters: (filters: Partial<AgentFilters>) => void;
  resetFilters: () => void;

  // Computed
  getFilteredAgents: () => Agent[];
  getSelectedAgent: () => Agent | null;
  getAgentMetrics: (agentId: string) => AgentMetrics | null;
  getUpdateState: (agentId: string) => AgentUpdateState | null;
  getServiceEvent: (agentId: string) => ServiceEventData | null;
}

const FILTERS_STORAGE_KEY = 'ra.agent.filters';

const defaultFilters: AgentFilters = {
  search: '',
  showOffline: true,
  sortBy: 'hostname',
  sortOrder: 'asc',
  viewMode: 'grid',
};

const loadFilters = (): AgentFilters => {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return defaultFilters;
    const parsed = JSON.parse(raw) as Partial<AgentFilters> | null;
    return { ...defaultFilters, ...(parsed || {}) };
  } catch (_e) {
    return defaultFilters;
  }
};

export const useAgentsStore = create<AgentsState>((set, get) => ({
  // Initial state
  agents: [],
  selectedAgentId: null,
  agentMetrics: {},
  updateStates: {},
  serviceEvents: {},
  revokedAgentIds: {},
  lifecycleRevision: 0,
  filters: loadFilters(),

  // Actions
  setAgents: (agents, lifecycleRevision, canonical = false) =>
    set((state) => {
      // Only a versioned server snapshot can replace lifecycle state. This
      // makes delayed legacy events harmless while allowing a later durable
      // snapshot to reintroduce an explicitly re-approved installation.
      // An unversioned REST response can have started before DELETE and finish
      // afterwards. It is never allowed to replace a locally revoked set. A
      // versioned WS snapshot remains the convergence mechanism.
      if (lifecycleRevision === undefined && Object.keys(state.revokedAgentIds).length > 0)
        return state;
      if (lifecycleRevision !== undefined && lifecycleRevision < state.lifecycleRevision)
        return state;
      if (!canonical && lifecycleRevision === undefined) return state;
      const revokedAgentIds = { ...state.revokedAgentIds };
      // A same-revision snapshot can predate (or race) DELETE. Only a later
      // authoritative revision is permitted to represent re-approval.
      if (lifecycleRevision !== undefined) {
        for (const agent of agents) {
          if (lifecycleRevision > (revokedAgentIds[agent.agentId] ?? -1)) {
            delete revokedAgentIds[agent.agentId];
          }
        }
      }
      const visibleAgents = agents.filter((agent) => !revokedAgentIds[agent.agentId]);
      return {
        agents: visibleAgents,
        revokedAgentIds,
        lifecycleRevision: lifecycleRevision ?? state.lifecycleRevision,
      };
    }),

  updateAgent: (agent) =>
    set((state) => {
      const index = state.agents.findIndex((a) => a.agentId === agent.agentId);
      if (state.revokedAgentIds[agent.agentId]) return state;
      if (index >= 0) {
        const newAgents = [...state.agents];
        newAgents[index] = agent;
        return { agents: newAgents };
      }
      return { agents: [...state.agents, agent] };
    }),

  updateAgentMetrics: (agentId, metrics) =>
    set((state) => {
      if (state.revokedAgentIds[agentId]) return state;
      return { agentMetrics: { ...state.agentMetrics, [agentId]: metrics } };
    }),

  setUpdateState: (agentId, state) =>
    set((prev) =>
      prev.revokedAgentIds[agentId]
        ? prev
        : { updateStates: { ...prev.updateStates, [agentId]: state } }
    ),

  clearUpdateState: (agentId) =>
    set((prev) => {
      const next = { ...prev.updateStates };
      delete next[agentId];
      return { updateStates: next };
    }),

  setServiceEvent: (agentId, event) =>
    set((prev) =>
      prev.revokedAgentIds[agentId]
        ? prev
        : { serviceEvents: { ...prev.serviceEvents, [agentId]: event } }
    ),

  clearServiceEvent: (agentId) =>
    set((prev) => {
      const next = { ...prev.serviceEvents };
      delete next[agentId];
      return { serviceEvents: next };
    }),

  updateAgentServiceStatus: (agentId, status) =>
    set((state) => {
      if (state.revokedAgentIds[agentId]) return state;
      return {
        agents: state.agents.map((a) =>
          a.agentId === agentId ? { ...a, serviceStatus: status } : a
        ),
      };
    }),

  removeAgent: (agentId, lifecycleRevision) =>
    set((state) => {
      // A delayed revocation event cannot undo a later re-approval snapshot.
      if (lifecycleRevision !== undefined && lifecycleRevision < state.lifecycleRevision)
        return state;
      const revision = lifecycleRevision ?? state.lifecycleRevision;
      return {
        agents: state.agents.filter((a) => a.agentId !== agentId),
        selectedAgentId: state.selectedAgentId === agentId ? null : state.selectedAgentId,
        revokedAgentIds: {
          ...state.revokedAgentIds,
          [agentId]: revision,
        },
        agentMetrics: Object.fromEntries(
          Object.entries(state.agentMetrics).filter(([id]) => id !== agentId)
        ),
        updateStates: Object.fromEntries(
          Object.entries(state.updateStates).filter(([id]) => id !== agentId)
        ),
        serviceEvents: Object.fromEntries(
          Object.entries(state.serviceEvents).filter(([id]) => id !== agentId)
        ),
        lifecycleRevision: Math.max(state.lifecycleRevision, revision),
      };
    }),

  restoreAgent: (agentId) =>
    set((state) => {
      const revokedAgentIds = { ...state.revokedAgentIds };
      delete revokedAgentIds[agentId];
      return { revokedAgentIds };
    }),

  selectAgent: (agentId) => set({ selectedAgentId: agentId }),

  setFilters: (filters) =>
    set((state) => {
      const next = { ...state.filters, ...filters };
      try {
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(next));
      } catch (_e) {
        // ignore storage errors
      }
      return { filters: next };
    }),

  resetFilters: () => {
    try {
      localStorage.removeItem(FILTERS_STORAGE_KEY);
    } catch (_e) {
      // ignore
    }
    set({ filters: defaultFilters });
  },

  // Computed
  getFilteredAgents: () => {
    const { agents, filters } = get();
    let filtered = [...agents];

    // Filter by connection status
    if (!filters.showOffline) {
      filtered = filtered.filter((a) => a.connected);
    }

    // Filter by search term
    if (filters.search) {
      const term = filters.search.toLowerCase();
      filtered = filtered.filter((a) => {
        const searchable = [a.hostname, a.agentId, a.os, a.arch, a.version]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return searchable.includes(term);
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case 'hostname': {
          const aName = (a.hostname || a.agentId).toLowerCase();
          const bName = (b.hostname || b.agentId).toLowerCase();
          comparison = aName.localeCompare(bName);
          break;
        }
        case 'status': {
          comparison = a.connected === b.connected ? 0 : a.connected ? -1 : 1;
          break;
        }
        case 'lastSeen': {
          const aTime = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
          const bTime = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
          comparison = bTime - aTime; // Most recent first
          break;
        }
      }

      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  },

  getSelectedAgent: () => {
    const { agents, selectedAgentId } = get();
    if (!selectedAgentId) return null;
    return agents.find((a) => a.agentId === selectedAgentId) || null;
  },

  getAgentMetrics: (agentId) => {
    const { agentMetrics } = get();
    return agentMetrics[agentId] || null;
  },

  getUpdateState: (agentId) => {
    const { updateStates } = get();
    return updateStates[agentId] || null;
  },

  getServiceEvent: (agentId) => {
    const { serviceEvents } = get();
    return serviceEvents[agentId] || null;
  },
}));
